import { HealthFeedState, IndicatorState, ServiceStatus } from '@/types';
import { getIndicator } from '@/services/indicators';
import { useAdminAuth } from '@/hooks';
import { ConnectingIcon, StatusIcon } from './StatusIcon';

interface HeaderProps {
  /** The aggregate verdict. Only trusted when feedState is 'live'. */
  status: ServiceStatus;
  /** How much we actually know. */
  feedState: HealthFeedState;
}

export function Header({ status, feedState }: HeaderProps) {
  const { user, isAdmin, authError, busy, signIn, signOut } = useAdminAuth();

  return (
    <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/90 backdrop-blur">
      <div className="mx-auto flex max-w-4xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
        <div className="flex min-w-0 items-center gap-2.5">
          <SometaMark />
          <span className="truncate text-sm font-semibold text-slate-900">
            Someta Status
          </span>
        </div>

        <div className="flex shrink-0 items-center gap-2 sm:gap-3">
          {authError && (
            <span className="hidden text-xs text-red-600 sm:inline">{authError}</span>
          )}
          {/* Sign-in must live HERE, not inside an error row: error rows only
              render once the feed loads, and the feed only loads for a
              signed-in admin, so the gate used to hide its own key. */}
          {isAdmin ? (
            <button
              onClick={signOut}
              className="text-xs font-medium text-slate-500 transition-colors hover:text-slate-900"
              title={user?.email ?? undefined}
            >
              Sign out
            </button>
          ) : (
            <button
              onClick={signIn}
              disabled={busy}
              className="rounded-md border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-700 transition-colors hover:border-slate-300 hover:bg-slate-50 disabled:opacity-50"
            >
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
          )}
          <HeaderPill status={status} feedState={feedState} />
        </div>
      </div>
      {authError && (
        <p className="px-4 pb-2 text-xs text-red-600 sm:hidden">{authError}</p>
      )}
    </header>
  );
}

/**
 * The verdict in short form, so it survives scrolling past the banner.
 *
 * Deliberately the ONE-WORD label, not the headline sentence. Both elements sit
 * within ~150px of each other above the fold, and printing "Some systems
 * degraded" twice read as a stutter rather than as emphasis. The banner states
 * the verdict; this only has to keep it on screen.
 *
 * Branches on feedState first: when the feed is unreadable this says "Status
 * Unavailable" in slate, never the green pill that a zero-error empty result
 * would otherwise produce.
 */
function HeaderPill({ status, feedState }: HeaderProps) {
  if (feedState === 'connecting') {
    return (
      <span className="flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1">
        <ConnectingIcon className="h-3.5 w-3.5" />
        <span className="text-xs font-medium text-slate-600">Checking…</span>
      </span>
    );
  }

  const state: IndicatorState = feedState === 'unavailable' ? 'unknown' : status;
  const indicator = getIndicator(state);

  // "Status Unavailable" is never abbreviated, at any width. The healthy and
  // degraded labels may shorten on a phone because their one-word form still
  // says the same thing; "Unknown" on its own is quieter than the situation
  // warrants, and this pill is the one element guaranteed to stay on screen.
  if (state === 'unknown') {
    return (
      <span className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 ${indicator.surface}`}>
        <StatusIcon state="unknown" className="h-3.5 w-3.5" />
        <span className={`text-xs font-medium ${indicator.text}`}>Status Unavailable</span>
      </span>
    );
  }

  return (
    <span
      className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 ${indicator.surface}`}
    >
      <StatusIcon state={state} className="h-3.5 w-3.5" />
      <span className={`text-xs font-medium ${indicator.text}`}>{indicator.label}</span>
    </span>
  );
}

function SometaMark() {
  // The real brand mark, not a generic chart glyph. This page is the one
  // customers land on when something looks broken; it should obviously belong
  // to Someta rather than to a template.
  return (
    <img
      src="/someta-mark.png"
      alt=""
      className="h-6 w-6 shrink-0 rounded-[6px]"
      aria-hidden
    />
  );
}
